import {memo, useState, useCallback, type ReactNode} from "react";
import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  IconButton,
  NumberInput,
  Separator,
  Stack,
  Tag,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  AdjustmentsHorizontalIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import {Button} from "../../../../../components/ui/button";
import {ASSERTIONS, COTABDS} from "../mockData";
import type {COTABD, CollaborationProps, FinancialStatementArea} from "../types";
import {COTABD_TYPE_COLORS} from "../types";
import {ReviewsWidget, CommentsWidget} from "./CollaborationWidget";
import {StatusButton, LockToggle, ExportButton} from "./ElcHeader";
import {DataGrid} from "./DataGrid";

// --- Shared Components (exported for use by other FSA pages) ---

export const MockFilterBar = memo(
  ({total, px = 6, showPagination = true}: {total: number; px?: number; showPagination?: boolean}) => (
    <Flex align="center" px={px} py={2} borderBottom="1px solid {colors.gray.200}" gap={3}>
      <Button size="xs" colorPalette="gray" variant="solid" bg="gray.800" color="white" _hover={{bg: "gray.700"}}>
        Default view
        <Box as={ChevronDownIcon} boxSize="14px" />
      </Button>
      <HStack gap={1} color="gray.800" cursor="pointer" _hover={{color: "gray.900"}}>
        <Box as={FunnelIcon} boxSize="13px" />
        <Text fontSize="xs" fontWeight="semibold">
          Filter...
        </Text>
      </HStack>
      <Flex flex={1} />
      <HStack gap={1} color="gray.800" cursor="pointer" _hover={{color: "gray.900"}}>
        <Box as={AdjustmentsHorizontalIcon} boxSize="13px" />
        <Text fontSize="xs" fontWeight="semibold">
          Display
        </Text>
      </HStack>
      {showPagination && (
        <>
          <Separator orientation="vertical" h="20px" />
          <HStack gap={1.5} color="gray.800">
            <Box as="button" cursor="pointer" _hover={{color: "gray.900"}}>
              <Box as={ChevronLeftIcon} boxSize="13px" />
            </Box>
            <Text fontSize="xs" fontWeight="semibold" whiteSpace="nowrap">
              {total > 0 ? `1 - ${total} of ${total}` : "0 of 0"}
            </Text>
            <Box as="button" cursor="pointer" _hover={{color: "gray.900"}}>
              <Box as={ChevronRightIcon} boxSize="13px" />
            </Box>
          </HStack>
        </>
      )}
    </Flex>
  ),
);

export const FsaCotabdHeader = memo(
  ({
    fsaName,
    allocatedMateriality,
    trivialThreshold,
    fsaSignificance,
    ...collaborationProps
  }: {
    fsaName: string;
    allocatedMateriality?: number;
    trivialThreshold?: number;
    fsaSignificance?: ReactNode;
  } & CollaborationProps) => {
    const hasMaterialityRow = allocatedMateriality != null || trivialThreshold != null || fsaSignificance != null;
    return (
      <Box px={6} py={4} borderBottom="1px solid {colors.gray.200}">
        <Flex align="flex-start" gap={2}>
          <Stack flex={1} gap={1}>
            <Heading size="sm" color="gray.800">
              {fsaName}
            </Heading>
            {hasMaterialityRow && (
              <HStack gap={2}>
                {allocatedMateriality != null && (
                  <Tag.Root colorPalette="gray">
                    <Tag.Label>Materiality: ${allocatedMateriality.toLocaleString()}</Tag.Label>
                  </Tag.Root>
                )}
                {trivialThreshold != null && (
                  <Tag.Root colorPalette="gray">
                    <Tag.Label>Trivial: ${trivialThreshold.toLocaleString()}</Tag.Label>
                  </Tag.Root>
                )}
                {fsaSignificance}
              </HStack>
            )}
          </Stack>
          <ReviewsWidget signOffs={collaborationProps.signOffs} onOpen={collaborationProps.onOpenCollaboration} />
          <CommentsWidget
            messages={collaborationProps.messages}
            unreadCount={collaborationProps.unreadCount}
            onOpen={collaborationProps.onOpenCollaboration}
          />
          <StatusButton status={collaborationProps.status} onStatusChange={collaborationProps.onStatusChange} />
          <LockToggle
            locked={collaborationProps.locked}
            lockedBy={collaborationProps.lockedBy}
            lockedAt={collaborationProps.lockedAt}
            onToggle={collaborationProps.onLockChange}
          />
          <ExportButton selection={collaborationProps.selection} onOpenReport={collaborationProps.onOpenReport} />
        </Flex>
      </Box>
    );
  },
);

// --- Scoping Page ---

const GRID_COLUMNS = "200px 90px 160px 150px 1fr 200px 40px";

type CotabdOverride = {allocatedMateriality?: number; trivialThreshold?: number; justification?: string};

export const FsaScoping = memo(
  ({
    fsa,
    allocatedMateriality,
    trivialThreshold,
    fsaSignificance,
    ...collaborationProps
  }: {
    fsa: FinancialStatementArea;
    allocatedMateriality?: number;
    trivialThreshold?: number;
    fsaSignificance?: ReactNode;
  } & CollaborationProps) => {
    const cotabds = COTABDS.filter(c => c.fsaId === fsa.id);
    const [overrides, setOverrides] = useState<Record<string, CotabdOverride>>({});

    const updateOverride = useCallback((cotabdId: string, patch: Partial<CotabdOverride>) => {
      setOverrides(prev => ({...prev, [cotabdId]: {...prev[cotabdId], ...patch}}));
    }, []);

    return (
      <Stack gap={0} minW={0}>
        <FsaCotabdHeader
          fsaName={fsa.name}
          {...collaborationProps}
          allocatedMateriality={allocatedMateriality}
          trivialThreshold={trivialThreshold}
          fsaSignificance={fsaSignificance}
        />

        {/* Actions bar */}
        <DataGrid.ActionBar>
          <Button
            size="xs"
            variant="outline"
            bg="white"
            borderColor="gray.200"
            color="gray.700"
            _hover={{bg: "gray.50"}}
            onClick={e => e.stopPropagation()}
          >
            <Box as={PlusIcon} boxSize="14px" />
            Add COTABD
          </Button>
        </DataGrid.ActionBar>

        {/* Filter bar */}
        <Box bg="gray.50">
          <MockFilterBar total={cotabds.length} showPagination={false} />
        </Box>

        {/* Column headers */}
        <DataGrid.Header columns={GRID_COLUMNS}>
          <Text>Name</Text>
          <Text>Significant</Text>
          <Text>Alloc. Materiality</Text>
          <Text>Trivial</Text>
          <Text>Justification</Text>
          <Text pl={4}>Assertions</Text>
          <Box />
        </DataGrid.Header>

        {/* COTABD rows */}
        {cotabds.map(cotabd => (
          <CotabdScopingRow
            key={cotabd.id}
            cotabd={cotabd}
            fsaAllocatedMateriality={allocatedMateriality}
            fsaTrivialThreshold={trivialThreshold}
            override={overrides[cotabd.id]}
            onUpdateOverride={patch => updateOverride(cotabd.id, patch)}
          />
        ))}
      </Stack>
    );
  },
);

const formatCurrency = (n: number): string => `$${n.toLocaleString()}`;

const CotabdScopingRow = memo(
  ({
    cotabd,
    fsaAllocatedMateriality,
    fsaTrivialThreshold,
    override,
    onUpdateOverride,
  }: {
    cotabd: COTABD;
    fsaAllocatedMateriality?: number;
    fsaTrivialThreshold?: number;
    override?: CotabdOverride;
    onUpdateOverride: (patch: Partial<CotabdOverride>) => void;
  }) => {
    const assertions = ASSERTIONS.filter(a => cotabd.assertionIds.includes(a.id));

    const hasAllocOverride = override?.allocatedMateriality != null;
    const hasTrivialOverride = override?.trivialThreshold != null;

    return (
      <DataGrid.Row columns={GRID_COLUMNS} alignItems="start" borderVariant="always" clickable={false}>
        {/* Name + Type combined */}
        <Box>
          <Text fontSize="sm" fontWeight="medium">
            {cotabd.name}
          </Text>
          <Badge colorPalette={COTABD_TYPE_COLORS[cotabd.type]} size="xs" textTransform="capitalize" mt={1}>
            {cotabd.type}
          </Badge>
        </Box>
        <Box>
          <Badge colorPalette={cotabd.significant ? "green" : "gray"} size="sm" variant="subtle" cursor="pointer">
            {cotabd.significant ? "Yes" : "No"}
          </Badge>
        </Box>
        {/* Allocated Materiality override */}
        <Box>
          {hasAllocOverride ? (
            <HStack gap={1} align="center">
              <Text fontSize="sm" color="gray.600">
                $
              </Text>
              <NumberInput.Root
                variant="outline"
                size="sm"
                w="110px"
                min={0}
                step={10_000}
                value={String(override!.allocatedMateriality)}
                onValueChange={d => {
                  const v = parseFloat(d.value);
                  if (!isNaN(v)) onUpdateOverride({allocatedMateriality: v});
                }}
              >
                <NumberInput.Input />
              </NumberInput.Root>
            </HStack>
          ) : (
            <HStack
              gap={1}
              align="center"
              px={2}
              py={1}
              borderRadius="md"
              border="1px dashed {colors.gray.300}"
              cursor="pointer"
              _hover={{borderColor: "gray.400", bg: "gray.50"}}
              onClick={() => onUpdateOverride({allocatedMateriality: fsaAllocatedMateriality ?? 0})}
              w="fit-content"
            >
              <Text fontSize="sm" color="gray.400">
                {fsaAllocatedMateriality != null ? formatCurrency(fsaAllocatedMateriality) : "—"}
              </Text>
            </HStack>
          )}
        </Box>
        {/* Trivial Threshold override */}
        <Box>
          {hasTrivialOverride ? (
            <HStack gap={1} align="center">
              <Text fontSize="sm" color="gray.600">
                $
              </Text>
              <NumberInput.Root
                variant="outline"
                size="sm"
                w="100px"
                min={0}
                step={1_000}
                value={String(override!.trivialThreshold)}
                onValueChange={d => {
                  const v = parseFloat(d.value);
                  if (!isNaN(v)) onUpdateOverride({trivialThreshold: v});
                }}
              >
                <NumberInput.Input />
              </NumberInput.Root>
            </HStack>
          ) : (
            <HStack
              gap={1}
              align="center"
              px={2}
              py={1}
              borderRadius="md"
              border="1px dashed {colors.gray.300}"
              cursor="pointer"
              _hover={{borderColor: "gray.400", bg: "gray.50"}}
              onClick={() => onUpdateOverride({trivialThreshold: fsaTrivialThreshold ?? 0})}
              w="fit-content"
            >
              <Text fontSize="sm" color="gray.400">
                {fsaTrivialThreshold != null ? formatCurrency(fsaTrivialThreshold) : "—"}
              </Text>
            </HStack>
          )}
        </Box>
        {/* Justification */}
        <Textarea
          size="sm"
          fontSize="sm"
          autoresize
          minH="32px"
          placeholder="Add justification..."
          value={override?.justification ?? ""}
          onChange={e => onUpdateOverride({justification: e.target.value})}
        />
        {/* Assertions stacked */}
        <Flex gap={1} flexWrap="wrap" pl={4}>
          {assertions.map(a => (
            <Badge key={a.id} size="sm" variant="outline" colorPalette="gray">
              {a.name}
            </Badge>
          ))}
        </Flex>
        <IconButton
          aria-label="Remove COTABD"
          size="xs"
          variant="ghost"
          colorPalette="gray"
          onClick={e => e.stopPropagation()}
        >
          <Box as={XMarkIcon} boxSize="14px" />
        </IconButton>
      </DataGrid.Row>
    );
  },
);
